import PropTypes from 'prop-types';
import cn from 'classnames';

import ProductForm from './ProductForm';

import panelStyles from './styles.module.scss';

const ReviewContext = ({
  handleSubmitReview,
  isFormValid,
  productDescription,
  setProductDescription,
  title,
  setTitle,
  audience,
  setAudience,
  businessGoal,
  setBusinessGoal,
  isSubmitting,
  goToPrevPage,
  selectedCategory,
  setSelectedCategory,
  selectedProblemChips,
  setSelectedProblemChips,
  // AI inference props
  aiFieldStates,
  isLoadingAiSuggestions,
  hasAiSuggestions,
  onRestoreAiSuggestions,
  onFieldEdit,
  onRestoreFieldAi,
  // Analytics props
  analyticsUploadState,
  analyticsFiles,
  onAnalyticsUpload,
  onAnalyticsRemove,
  disabled,
  observedAnalytics,
  setObservedAnalytics,
  // Persona props
  personas,
  onCreatePersona,
  onSelectPersona,
  onDeselectPersona,
  selectedPersona,
  isGeneratingPersona,
  isViewer,
}) => (
  <div className={panelStyles.fullWidthStageCard}>
    <div className="flex-1 min-h-0 overflow-auto">
      <ProductForm
        productDescription={productDescription}
        setProductDescription={setProductDescription}
        title={title}
        setTitle={setTitle}
        audience={audience}
        setAudience={setAudience}
        businessGoal={businessGoal}
        setBusinessGoal={setBusinessGoal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedProblemChips={selectedProblemChips}
        setSelectedProblemChips={setSelectedProblemChips}
        aiFieldStates={aiFieldStates}
        isLoadingAiSuggestions={isLoadingAiSuggestions}
        hasAiSuggestions={hasAiSuggestions}
        onRestoreAiSuggestions={onRestoreAiSuggestions}
        onFieldEdit={onFieldEdit}
        onRestoreFieldAi={onRestoreFieldAi}
        analyticsUploadState={analyticsUploadState}
        analyticsFiles={analyticsFiles}
        onAnalyticsUpload={onAnalyticsUpload}
        onAnalyticsRemove={onAnalyticsRemove}
        disabled={disabled}
        observedAnalytics={observedAnalytics}
        setObservedAnalytics={setObservedAnalytics}
        personas={personas}
        onCreatePersona={onCreatePersona}
        onSelectPersona={onSelectPersona}
        onDeselectPersona={onDeselectPersona}
        selectedPersona={selectedPersona}
        isGeneratingPersona={isGeneratingPersona}
        isViewer={isViewer}
      />
    </div>

    <div className="flex items-center justify-between flex-shrink-0 border-t border-[#F1F2F5] p-4 px-7 2xl:p-6">
      <button
        className={cn(
          'flex items-center px-20 gap-8 h-10 !bg-white border border-[#E8E8E8] rounded-lg',
          'cursor-pointer font-medium text-sm text-[#121723]'
        )}
        onClick={goToPrevPage}
      >
        Back
      </button>
      {/* Submit Button */}
      <button
        disabled={!isFormValid || isSubmitting || isGeneratingPersona}
        onClick={handleSubmitReview}
        className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors ${
          isFormValid && !isSubmitting && !isGeneratingPersona
            ? 'bg-primary-500 text-white cursor-pointer hover:bg-primary-600'
            : 'bg-gray-100 text-gray-600 cursor-not-allowed'
        }`}
        style={{ minWidth: '158px', height: '40px', padding: '10px 20px' }}
      >
        Submit for audit
      </button>
    </div>
  </div>
);

ReviewContext.propTypes = {
  handleSubmitReview: PropTypes.func.isRequired,
  isFormValid: PropTypes.bool.isRequired,
  productDescription: PropTypes.string.isRequired,
  setProductDescription: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  setTitle: PropTypes.func.isRequired,
  audience: PropTypes.string.isRequired,
  setAudience: PropTypes.func.isRequired,
  goToPrevPage: PropTypes.func.isRequired,
  businessGoal: PropTypes.string.isRequired,
  setBusinessGoal: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  selectedCategory: PropTypes.string.isRequired,
  setSelectedCategory: PropTypes.func.isRequired,
  selectedProblemChips: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedProblemChips: PropTypes.func.isRequired,
  aiFieldStates: PropTypes.shape({
    title: PropTypes.string,
    audience: PropTypes.string,
    problem: PropTypes.string,
    businessGoal: PropTypes.string,
  }),
  isLoadingAiSuggestions: PropTypes.bool,
  hasAiSuggestions: PropTypes.bool,
  onRestoreAiSuggestions: PropTypes.func,
  onFieldEdit: PropTypes.func,
  onRestoreFieldAi: PropTypes.func,
  analyticsUploadState: PropTypes.oneOf([null, 'uploading', 'in-progress', 'done']),
  analyticsFiles: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    previewUrl: PropTypes.string,
  })),
  onAnalyticsUpload: PropTypes.func,
  onAnalyticsRemove: PropTypes.func,
  disabled: PropTypes.bool,
  observedAnalytics: PropTypes.string,
  setObservedAnalytics: PropTypes.func,
  personas: PropTypes.array,
  onCreatePersona: PropTypes.func,
  onSelectPersona: PropTypes.func,
  onDeselectPersona: PropTypes.func,
  selectedPersona: PropTypes.object,
  isGeneratingPersona: PropTypes.bool,
  isViewer: PropTypes.bool,
};

ReviewContext.defaultProps = {
  aiFieldStates: null,
  isLoadingAiSuggestions: false,
  hasAiSuggestions: false,
  onRestoreAiSuggestions: () => {},
  onFieldEdit: null,
  onRestoreFieldAi: () => {},
  analyticsUploadState: null,
  analyticsFiles: null,
  onAnalyticsUpload: () => {},
  onAnalyticsRemove: () => {},
  disabled: false,
  observedAnalytics: '',
  setObservedAnalytics: () => {},
  personas: [],
  onCreatePersona: () => {},
  onSelectPersona: () => {},
  onDeselectPersona: () => {},
  selectedPersona: null,
  isGeneratingPersona: false,
  isViewer: false,
};

export default ReviewContext;
